import React, { useRef, useEffect } from 'react';
import styled from 'styled-components';
// import { MarketData } from '../../utils/marketData';
import type { MarketData } from '../../types';

const TickerContainer = styled.div`
  width: 100%;
  overflow: hidden;
  background-color: #ffffff;
  border-top: 1px solid #f0f0f0;
  border-bottom: 1px solid #f0f0f0;
  padding: 0.75rem 0;
  position: fixed;
  bottom: 0;
  left: 0;
  z-index: 10;
`;

const TickerTrack = styled.div`
  display: flex;
  white-space: nowrap;
`;

const TickerItem = styled.div`
  display: flex;
  align-items: center;
  margin-right: 2rem;
  padding: 0 1rem;
`;

const IndexName = styled.span`
  font-weight: 600;
  margin-right: 0.75rem;
`;

const IndexValue = styled.span<{ status: 'up' | 'down' | 'closed' }>`
  color: ${(props) => {
    switch (props.status) {
      case 'up':
        return '#00C853';
      case 'down':
        return '#FF3D00';
      default:
        return '#757575';
    }
  }};
  font-weight: 500;
`;

interface MarketTickerProps {
  markets: MarketData[];
}

const MarketTicker: React.FC<MarketTickerProps> = ({ markets }) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number>(0);
  const scrollSpeed = 0.5; // pixels per frame
  const position = useRef(0);

  // Create a duplicate set of markets for seamless scrolling
  const allMarkets = [...markets, ...markets, ...markets];

  useEffect(() => {
    let trackWidth = 0;

    const animate = () => {
      if (trackRef.current) {
        if (trackWidth === 0) {
          // Initialize widths on first run
          const trackElement = trackRef.current;
          trackWidth = trackElement.scrollWidth / 3; // Divide by 3 because we tripled the content
        }

        // Update position
        position.current -= scrollSpeed;

        // Reset position when first set of markets scrolls out of view
        if (Math.abs(position.current) >= trackWidth) {
          position.current = 0;
        }

        // Apply the transform
        trackRef.current.style.transform = `translateX(${position.current}px)`;
      }

      // Continue animation
      animationRef.current = requestAnimationFrame(animate);
    };

    // Start animation
    animationRef.current = requestAnimationFrame(animate);

    // Cleanup
    return () => {
      cancelAnimationFrame(animationRef.current);
    };
  }, [markets]);

  return (
    <TickerContainer>
      <TickerTrack ref={trackRef}>
        {allMarkets.map((market) => (
          <TickerItem key={market.id}>
            <IndexName>{market.name}</IndexName>
            <IndexValue status={market.status}>
              {market.value.toFixed(2)}
              {' '}
              {market.change > 0 ? '+' : ''}
              {market.changePercent.toFixed(2)}
              %
            </IndexValue>
          </TickerItem>
        ))}
      </TickerTrack>
    </TickerContainer>
  );
};

export default MarketTicker;
